import { apiClient, getErrorMessage } from '@/lib/apiClient';

// ─── Types matching ProcessTrackerController DTOs ────────────────────────────

export interface ProcessMapState {
  name: string;
  isInitial: boolean;
  isTerminal: boolean;
}

export interface ProcessMapRule {
  ruleKey: string;
  displayName: string | null;
  isEnabled: boolean;
  isDraft: boolean;
}

export interface ProcessMapApprovalGate {
  isGated: boolean;
  policyName: string | null;
}

export interface ProcessMapWorkflowStep {
  name: string;
  stepType: string;
}

export interface ProcessMapWorkflowChain {
  workflowName: string;
  subscriptionEnabled: boolean;
  hasApprovalGate: boolean;
  steps: ProcessMapWorkflowStep[];
}

export interface ProcessMapEventChain {
  eventName: string;
  eventFullType: string;
  routingDecision: string | null;
  workflows: ProcessMapWorkflowChain[];
}

export interface ProcessMapTransition {
  key: string;
  /** The state machine (bound entity field) this transition belongs to. */
  stateMachine: string;
  fromState: string;
  toState: string;
  permission: string | null;
  type: string | null;
  description: string | null;
  rules: ProcessMapRule[];
  approvalGate: ProcessMapApprovalGate;
  eventChains: ProcessMapEventChain[];
}

export interface ProcessMapApprovalStepDefinition {
  name: string | null;
  approvalPattern: string | null;
  requiredApprovalCount: number | null;
  assignmentStrategy: string | null;
  requiredPermission: string | null;
  assignedToUserId: string | null;
  assignedToRoleName: string | null;
  resolveFromField: string | null;
  fallbackStrategy: string | null;
  timeoutDuration: string | null;
  timeoutAction: string | null;
  escalationPermission: string | null;
}

export interface ProcessMapApprovalPolicy {
  id: string;
  name: string;
  isEnabled: boolean;
  priority: number;
  triggerEvent: string | null;
  tenantId: string | null;
  steps: ProcessMapApprovalStepDefinition[];
}

export interface ProcessMapSubscription {
  id: string;
  name: string;
  eventType: string;
  eventName: string;
  workflowDefinitionId: string;
  isEnabled: boolean;
  mode: string;
  tenantId: string | null;
}

/**
 * One state machine of the entity — an entity may carry several
 * (Multiple-State-Machines-Per-Entity.md §6.3), each named by the entity field it drives.
 */
export interface ProcessMapStateMachine {
  stateMachine: string;
  states: ProcessMapState[];
  transitions: ProcessMapTransition[];
}

export interface ProcessMap {
  entityType: string;
  module: string;
  tenantId: string | null;
  /** The entity's machines, the primary first. */
  stateMachines: ProcessMapStateMachine[];
  approvalPolicies: ProcessMapApprovalPolicy[];
  workflowSubscriptions: ProcessMapSubscription[];
}

// ─── Service ─────────────────────────────────────────────────────────────────

class ProcessMapService {
  async getProcessMap(entityType: string, tenantId?: string): Promise<ProcessMap> {
    try {
      const params: Record<string, string> = { entityType };
      if (tenantId) params.tenantId = tenantId;
      const response = await apiClient.get<ProcessMap>('/api/process-tracker/process-map', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }
}

export const processMapService = new ProcessMapService();
